import { useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns/format';
import { startOfMonth } from 'date-fns/startOfMonth';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@bloomays-lib/ui.shared';
import { DataGridV2, formatEuro } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2Column } from '@bloomays-lib/ui.shared/datagrid';
import { getPrimePendingReferenceDate } from '@bloomays-lib/utils.shared';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import { useMonthlyBloomieMargins } from '../../hooks/useMonthlyBloomieMargins';
import { downloadSalesTadMarginsExport } from '../../utils/salesTadMarginsExport.utils';
import { SALES_TAD_OPTIONS, buildSalesTadOptionLabel, type SalesTadOption } from '../../constants/salesTadMargins';
import type { SalesTadMarginRow } from '../../types/primes';

/** Première année civile avec marges mensuelles in-app (aligné sur les vues primes). */
const FIRST_MARGIN_YEAR = 2026;
const MIN_MONTH = new Date(FIRST_MARGIN_YEAR, 0, 1);

const SalesTadMargins = () => {
  const { t } = useTranslation(['salesTadMargins']);
  const [referenceDate, setReferenceDate] = useState<Date | null>(() => startOfMonth(getPrimePendingReferenceDate()));
  const [selected, setSelected] = useState<SalesTadOption[]>([]);

  const selectedEmails = useMemo(() => selected.map((bloomie) => bloomie.email), [selected]);

  const {
    marginByEmail,
    loading: marginsLoading,
    error: marginsError,
  } = useMonthlyBloomieMargins({
    emails: selectedEmails,
    referenceDate,
    onError: (queryError) => {
      errorLogger(queryError, { extraInfos: 'getMonthlyBloomieMargins failed on /marges-sales-tad' });
    },
  });

  const rows: SalesTadMarginRow[] = useMemo(
    () =>
      selected.map((bloomie) => {
        const margin = marginByEmail.get(bloomie.email.trim().toLowerCase());
        return {
          email: bloomie.email,
          firstname: bloomie.firstname,
          lastname: bloomie.lastname,
          displayName: buildSalesTadOptionLabel(bloomie),
          luccaId: bloomie.luccaId ?? null,
          totalFreelanceMarge: margin?.totalFreelanceMarge ?? 0,
          totalBillingMarge: margin?.totalBillingMarge ?? 0,
          totalMarge: margin?.totalMarge ?? 0,
        };
      }),
    [selected, marginByEmail],
  );

  const columns: DataGridV2Column<SalesTadMarginRow>[] = useMemo(
    () => [
      { field: 'displayName', header: t('columnBloomie'), flex: 1.4, minWidth: 200 },
      { field: 'email', header: t('columnEmail'), flex: 1.4, minWidth: 220 },
      {
        field: 'totalFreelanceMarge',
        header: t('columnFreelanceMargin'),
        align: 'right',
        headerAlign: 'right',
        minWidth: 150,
        variant: 'custom',
        render: (row) => formatEuro(row.totalFreelanceMarge),
      },
      {
        field: 'totalBillingMarge',
        header: t('columnCdiMargin'),
        align: 'right',
        headerAlign: 'right',
        minWidth: 150,
        variant: 'custom',
        render: (row) => formatEuro(row.totalBillingMarge),
      },
      {
        field: 'totalMarge',
        header: t('columnTotalMargin'),
        align: 'right',
        headerAlign: 'right',
        minWidth: 150,
        variant: 'custom',
        render: (row) => <TotalMargin>{formatEuro(row.totalMarge)}</TotalMargin>,
      },
    ],
    [t],
  );

  const canExport = referenceDate !== null && rows.length > 0 && !marginsLoading;

  const handleExport = () => {
    if (referenceDate === null || rows.length === 0) {
      notify('warning', t('exportEmpty'), undefined, { hideProgressBar: true, toastId: 'sales-tad-export-empty' });
      return;
    }
    try {
      downloadSalesTadMarginsExport(rows, referenceDate);
    } catch (exportError) {
      errorLogger(exportError as Error, { extraInfos: 'salesTadMargins XLS export failed' });
      notify('error', t('exportError'), undefined, { hideProgressBar: true, toastId: 'sales-tad-export-error' });
    }
  };

  return (
    <Container>
      <Intro variant="body2">{t('intro')}</Intro>

      <ToolbarRow>
        <FieldWrapper>
          <DatePicker
            label={t('monthLabel')}
            views={['year', 'month']}
            openTo="month"
            minDate={MIN_MONTH}
            maxDate={new Date()}
            value={referenceDate}
            handleChange={(date) => setReferenceDate(date ? startOfMonth(date) : null)}
            size="small"
            fullWidth
          />
        </FieldWrapper>

        <SelectWrapper>
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={SALES_TAD_OPTIONS}
            value={selected}
            onChange={(_event, value) => setSelected(value)}
            getOptionLabel={(option) => buildSalesTadOptionLabel(option)}
            isOptionEqualToValue={(option, value) => option.email === value.email}
            renderInput={(params) => (
              <TextField {...params} label={t('bloomiesLabel')} placeholder={t('bloomiesPlaceholder')} />
            )}
          />
        </SelectWrapper>

        <ExportWrapper>
          <Button
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={!canExport}
            onClick={handleExport}
          >
            {t('exportButton')}
          </Button>
        </ExportWrapper>
      </ToolbarRow>

      {marginsError ? <Message variant="body2">{t('loadingMarginsError')}</Message> : null}

      {selected.length === 0 ? (
        <Hint variant="body2">{t('noSelectionHint')}</Hint>
      ) : (
        <DataGridV2
          uniqueId="sales-tad-margins"
          rows={rows}
          columns={columns}
          loading={marginsLoading}
          loadingVariant="bar"
          getRowId={(row: SalesTadMarginRow) => row.email}
          rowGrouping={false}
          hideFooter={rows.length <= 25}
          pagination={rows.length > 25}
          pageSize={25}
        />
      )}

      {referenceDate ? (
        <MonthCaption variant="caption">{t('selectedMonth', { month: format(referenceDate, 'MM/yyyy') })}</MonthCaption>
      ) : null}
    </Container>
  );
};

export default SalesTadMargins;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const Intro = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const ToolbarRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const FieldWrapper = styled('div')(() => ({
  width: 200,
}));

const SelectWrapper = styled('div')(() => ({
  flex: 1,
  minWidth: 280,
}));

const ExportWrapper = styled('div')(() => ({
  marginLeft: 'auto',
}));

const TotalMargin = styled('span')(() => ({
  fontWeight: 600,
}));

const Hint = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
}));

const MonthCaption = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const Message = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
}));
